// models/api/wifi_nets.ts
import { getRequest, postRequest, putRequest, deleteRequest } from './rest'
import { ALL_ORGS_SELECTED } from 'lib/constants'
import type { WifiNet, WifiNetType } from 'lib/types/wifi_nets'

const orgHeader = (orgId: string) =>
  orgId === ALL_ORGS_SELECTED ? {} : { org_id: orgId }

// Build a stable id if backend doesn't provide one
const buildId = (n: any) => {
  const raw =
    n?.id ??
    n?.uuid ??
    n?.wifi_net_id ??
    // fallback: deterministic composite (org + ssid)
    `${n?.org_id ?? n?.orgId ?? 'no-org'}:${n?.ssid ?? 'no-ssid'}`
  return String(raw)
}

const normalize = (n: any): WifiNet => {
  const raw = n?.wifiNet ?? n // be tolerant to {wifiNet:{...}} or plain object
  return {
    ...raw,
    id: buildId(raw),
    orgId: raw?.org_id ?? raw?.orgId ?? '',
    ssid: raw?.ssid ?? '',
    type: (raw?.type ?? null) as WifiNetType | null,
    password: raw?.password ?? null,
    user: raw?.user ?? null,
  } as WifiNet
}

export interface WifiNetWriteFields {
  ssid: string
  type: WifiNetType
  password?: string | null
  user?: string | null
}

const buildWritePayload = (fields: WifiNetWriteFields) => {
  const payload: Record<string, unknown> = { ssid: fields.ssid, type: fields.type }
  if (fields.type === 'open') {
    payload.password = null
    payload.user = null
  } else if (fields.type === 'personal') {
    payload.password = fields.password ?? ''
    payload.user = null
  } else {
    payload.password = fields.password ?? ''
    payload.user = fields.user ?? ''
  }
  return payload
}

export const getWifiNets = async (orgId: string): Promise<WifiNet[]> => {
  const res = await getRequest({
    endpoint: 'api/v5/wifi_nets',
    header: orgHeader(orgId),
  })
  const list = res?.data?.data ?? []
  // ensure orgId present for skinny payloads
  return list.map((n: any) => normalize({ ...n, orgId }))
}

export const createWifiNet = async (orgId: string, fields: WifiNetWriteFields): Promise<WifiNet> => {
  const res = await postRequest({
    endpoint: 'api/v5/wifi_nets',
    header: orgHeader(orgId),
    data: { orgId, ...buildWritePayload(fields) },
  })
  const payload = res?.data?.data ?? res?.data
  return normalize(payload)
}

// PUT /api/v5/wifi_nets/{id}
export const updateWifiNet = async (
  id: string,
  orgId: string,
  fields: WifiNetWriteFields,
): Promise<WifiNet> => {
  const res = await putRequest({
    endpoint: `api/v5/wifi_nets/${id}`,
    header: orgHeader(orgId),
    data: { orgId, ...buildWritePayload(fields) },
  })
  const payload = res?.data?.data ?? res?.data
  return normalize(payload)
}

// DELETE /api/v5/wifi_nets/{id} -> 204
export const deleteWifiNet = async (id: string, orgId: string): Promise<void> => {
  await deleteRequest({
    endpoint: `api/v5/wifi_nets/${id}`,
    header: orgHeader(orgId),
  })
}
